import {
  getDeviceId,
  getPortalId,
  getSessionId,
  getUtk,
} from 'usage-tracker-core/common/defaultTrackers';
import { SESSION_REPLAY_ID_PROPERTY } from 'usage-tracker-core/constants';
import {
  LockedTrackerInstance,
  TrackerConfig,
} from 'usage-tracker-core/trackerTypes';

// Used to provide App Info to the Session Replay Tracking/Config Endpoint
const getAppInfo = () => ({
  // retrieves the deployable name if available from window context
  // @ts-ignore this is a global variable and we don't have control over it
  name: window.hubspot?.bender?.currentProject ?? 'unknown',
  // retrieves the deployable version if available from window context
  // @ts-ignore this is a global variable and we don't have control over it
  version: window.hubspot?.bender?.currentProjectVersion ?? 'unknown',
  // retrieves the portal id if available from the running context
  portalId: getPortalId() ?? 'unknown',
});

// Provides the `Authorization` Header (workaround) for providing relevant
// App Info and Portal ID to the Session Replay Tracking/Config Endpoint
/** @todo: this should be superseeded by Apmlitude's SDK `transportProvider` */
export const getAuthorizationKey = () =>
  `${getAppInfo().name}:::${getAppInfo().version}:::${getAppInfo().portalId}`;

// Tells us if the tracker is in standalone mode or not
export const isTrackerInStandaloneMode = <C extends TrackerConfig>(
  target: LockedTrackerInstance<C>
): target is LockedTrackerInstance<C & { standalone: true }> =>
  Boolean(target.getConfig().standalone);

// Since Amplitude Session Replay SDK only has a `getSessionId` method, we want to get the current
// registered Device Id and Session Id from Amplitude, so we can compare if they match the current Tracker
// and if not replace them with the current Tracker's Device Id and Session Id
export const extractAmplitudeIdentifiers = (
  amplitudeProperties: Record<string, any>
) => {
  const sessionReplayId = amplitudeProperties[SESSION_REPLAY_ID_PROPERTY];

  // If the property is not available, we return undefined for both Device Id and Session Id
  const [deviceId, sessionId] = sessionReplayId?.split('/') ?? [undefined, 0];

  return [deviceId, Number(sessionId)];
};

// Reads the latest deviceId and sessionId for the Session Replay ID, without
// advancing the tracker's identity/session counters.
export const getTrackerMetaProperties = (
  target: LockedTrackerInstance<TrackerConfig>,
  deviceIdFromTrackCall?: string
) => {
  const { deviceId: deviceIdFromConfig } = target.getConfig().properties;

  const deviceId = deviceIdFromTrackCall ?? deviceIdFromConfig ?? getDeviceId();
  const sessionId = getSessionId();

  return [getUtk() ?? deviceId, sessionId] as [string, number];
};
